import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import { ChatPresenceMap, ChatPresenceStatus, ChatUser } from '../../chat.model';

/**
 * CRM only (maqueta `Diretor.dc.html`): o painel "Atribuir a" com os
 * consultores da equipa e um botão "Atribuir" por linha. Só markup e @Output:
 * quem chama o servidor é o contentor. Mostrado só com `capabilities.canAssign`.
 *
 * `consultants` são as entradas de colegas da própria lista de conversas
 * (`role === 'AGENT'`, sem `supervised`), que o servidor já devolve por âmbito.
 * Desenho de `card` do tema: `card-header` com título e fechar, linhas com
 * avatar + presença e um `btn-soft-primary`.
 */
@Component({
  selector: 'chat-assign-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
<div class="chat-assign-panel">
  <div class="chat-assign-panel__head">
    <div class="chat-assign-panel__text">
      <h2 class="chat-assign-panel__title">{{ strings.assignTitle }}</h2>
      <p class="chat-assign-panel__sub">{{ strings.assignSubtitle }}</p>
    </div>
    <button type="button" class="chat-assign-panel__close" [attr.aria-label]="strings.closePanel" (click)="close.emit()">
      <chat-icon name="close" [size]="18"></chat-icon>
    </button>
  </div>
  <div class="chat-assign-panel__body">
    <p *ngIf="consultants.length === 0" class="chat-assign-panel__empty">{{ strings.assignEmpty }}</p>
    <div *ngFor="let c of consultants; trackBy: trackById" class="chat-assign-panel__row">
      <chat-avatar [src]="c.photo" [name]="c.name" [size]="36" [status]="presenceOf(c)"></chat-avatar>
      <div class="chat-assign-panel__who">
        <div class="chat-assign-panel__name">{{ c.name }}</div>
        <div class="chat-assign-panel__state" [class.chat-assign-panel__state--online]="presenceOf(c) === 'online'">{{ stateOf(c) }}</div>
      </div>
      <button type="button" class="chat-assign-panel__btn" (click)="assign.emit(c)">{{ strings.assign }}</button>
    </div>
  </div>
</div>`,
  styles: [`
:host { display: flex; flex-direction: column; min-height: 0; height: 100%; }
.chat-assign-panel { display: flex; flex-direction: column; min-height: 0; flex-grow: 1; background: var(--chat-surface); animation: chat-assign-panel-in .22s ease-out; }
.chat-assign-panel__head { display: flex; align-items: flex-start; gap: 8px; padding: 14px 16px; border-bottom: 1px solid var(--chat-line); }
.chat-assign-panel__text { flex-grow: 1; min-width: 0; }
.chat-assign-panel__title { margin: 0; font-size: 15px; font-weight: 600; color: var(--chat-heading); }
.chat-assign-panel__sub { margin: 2px 0 0; font-size: 12px; color: var(--chat-muted); }
.chat-assign-panel__close { width: 36px; height: 36px; margin: -6px -8px 0 0; border: 0; border-radius: var(--chat-radius); background: transparent; color: var(--chat-muted); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: background var(--chat-motion) ease, color var(--chat-motion) ease; }
.chat-assign-panel__close:hover { background: var(--chat-hover); color: var(--chat-ink); }
.chat-assign-panel__close:focus-visible { outline: 2px solid var(--chat-accent); outline-offset: -2px; }
.chat-assign-panel__body { flex-grow: 1; min-height: 0; overflow-y: auto; }
.chat-assign-panel__empty { margin: 0; padding: 16px; font-size: 13px; color: var(--chat-muted); }
.chat-assign-panel__row { display: flex; align-items: center; gap: 12px; padding: 10px 16px; border-bottom: 1px solid var(--chat-line); transition: background var(--chat-motion) ease; }
.chat-assign-panel__row:hover { background: var(--chat-hover); }
.chat-assign-panel__who { flex-grow: 1; min-width: 0; }
.chat-assign-panel__name { font-size: 13.5px; font-weight: 600; color: var(--chat-ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.chat-assign-panel__state { font-size: 11.5px; color: var(--chat-muted); min-height: 1em; }
.chat-assign-panel__state--online { color: var(--chat-success-ink); }
.chat-assign-panel__btn { height: 32px; padding: 0 12px; border: 0; border-radius: var(--chat-radius); background: rgba(var(--chat-accent-rgb), .12); color: var(--chat-accent); font: inherit; font-size: 12.5px; font-weight: 500; cursor: pointer; transition: background var(--chat-motion) ease, color var(--chat-motion) ease; }
.chat-assign-panel__btn:hover { background: var(--chat-accent); color: var(--chat-on-accent); }
.chat-assign-panel__btn:focus-visible { outline: 2px solid var(--chat-accent); outline-offset: 2px; }
@keyframes chat-assign-panel-in { from { opacity: 0; transform: translateX(12px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .chat-assign-panel { animation: none; } }
`]
})
export class ChatAssignPanelComponent {
  @Input() consultants: ChatUser[] = [];
  @Input() presence: ChatPresenceMap = {};
  @Input() online: string[] = [];
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;

  @Output() assign = new EventEmitter<ChatUser>();
  @Output() close = new EventEmitter<void>();

  trackById = (_i: number, c: ChatUser) => c.userId || c.name;

  presenceOf(c: ChatUser): ChatPresenceStatus | null {
    if (!c.userId) {
      return null;
    }
    const known = this.presence[c.userId];
    if (known) {
      return known;
    }
    return this.online.indexOf(c.userId) > -1 ? 'online' : null;
  }

  stateOf(c: ChatUser): string {
    const p = this.presenceOf(c);
    if (p === 'online') {
      return this.strings.presenceOnline;
    }
    if (p === 'away') {
      return this.strings.presenceAway;
    }
    return this.strings.presenceOffline;
  }
}
